import React, { useEffect, useRef, useState } from "react";
import {
  Layout,
  Menu,
  Drawer,
  Space,
  Badge,
  Flex,
  Image,
  Grid,
  Modal,
  Input,
  List,
  Pagination,
  Button,
  message,
} from "antd";
import {
  SearchOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  MenuOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useLocation } from "react-router-dom";

// import HeaderLogo from "../../../Images/Header/header-logo.png";
// import HeaderLogo from "../../../Images/Header/no-bg-header-logo.png";
import HeaderLogo from "../../../Images/Header/teamstar-new-logo-oct-2026.png";

import CartDrawer from "../../Common/cartDrawer";
import publicDataServices from "../../../APIs/publicDataServices";
import { settings } from "../../../Services/apiSetting";
import { getProductUrl } from "../../Common/helpers";

const { Header } = Layout;
const { useBreakpoint } = Grid;
const { Search } = Input;

const AppHeader = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const [messageApi, contextHolder] = message.useMessage();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchQuery, setSearchQuery] = useState(null);
  const [searchProducts, setSearchProducts] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchPagination, setSearchPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });
  const searchRequestId = useRef(0);
  const searchInputRef = useRef(null);

  const openSearch = () => {
    setDrawerOpen(false);
    setCartOpen(false);
    setSearchOpen(true);
  };

  const closeSearch = () => {
    searchRequestId.current += 1;
    setSearchOpen(false);
    setSearchValue("");
    setSearchQuery(null);
    setSearchProducts([]);
    setIsSearchLoading(false);
    setSearchPagination((prev) => ({ ...prev, current: 1, total: 0 }));
  };

  const getSearchProducts = async (value, page = 1) => {
    const requestId = ++searchRequestId.current;
    const query = value.trim();
    const pageSize = searchPagination.pageSize;

    setSearchQuery(query);
    setSearchProducts([]);
    setIsSearchLoading(true);

    try {
      const response = await publicDataServices.getProducts(
        (page - 1) * pageSize,
        pageSize,
        query,
        "cat-0",
        "date_asc",
      );

      if (requestId !== searchRequestId.current) return;

      if (response.data.statusCode === 200) {
        const data = JSON.parse(response.data.data);
        setSearchProducts(data.products);
        setSearchPagination((prev) => ({
          ...prev,
          current: page,
          total: data.total,
        }));
      } else {
        throw new Error(response.data.message || "Failed to fetch products");
      }
    } catch (error) {
      if (requestId !== searchRequestId.current) return;

      console.error("Error fetching products:", error);
      messageApi.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          error.message ||
          "Failed to fetch products",
      );
      setSearchPagination((prev) => ({ ...prev, current: 1, total: 0 }));
    } finally {
      if (requestId === searchRequestId.current) setIsSearchLoading(false);
    }
  };

  const menuItems = [
    { key: "/#promotion-section", label: "Promotions" },
    { key: "/products", label: "Products" },
    { key: "/catalogues", label: "Catalogue" },
    { key: "/company", label: "Company" },
    { key: "/locate-us", label: "Locate Us" },
    { key: "/contact-us", label: "Contact Us" },
    { key: "https://teamstarberhad.com/", label: "Corporate" },
  ].map((item) => ({
    ...item,
    label: item.key.startsWith("https://") ? (
      <a href={item.key} style={{ color: "inherit", display: "block" }}>
        {item.label}
      </a>
    ) : (
      <Link to={item.key} style={{ color: "inherit", display: "block" }}>
        {item.label}
      </Link>
    ),
  }));

  const handleNavigate = (key) => {
    navigate(key);
    setDrawerOpen(false);
  };

  const handleMenuClick = ({ domEvent }) => {
    // Ant Design can activate the menu item itself with Enter or a padding click.
    // Let the anchor handle navigation and modified clicks whenever it is targeted.
    if (
      !domEvent.target.closest("a[href]") &&
      !domEvent.ctrlKey && !domEvent.metaKey && !domEvent.shiftKey && !domEvent.altKey
    ) {
      domEvent.target.closest('[role="menuitem"]')?.querySelector("a[href]")?.click();
    }
    setDrawerOpen(false);
  };

  const [cartOpen, setCartOpen] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);

  useEffect(() => {
    const updateCount = () => {
      const cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
      setBadgeCount(cartItems.length);
    };

    updateCount();

    window.addEventListener("storage", updateCount);
    window.addEventListener("cartUpdated", updateCount);

    return () => {
      window.removeEventListener("storage", updateCount);
      window.removeEventListener("cartUpdated", updateCount);
    };
  }, [drawerOpen, cartOpen]);

  return (
    <Header
      style={{
        background: "#FFDC00",
        padding: "0 24px",
        height: 64,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      {contextHolder}
      <Flex justify="space-between" align="center" style={{ height: "100%" }}>
        {/* Left: Logo */}
        <Link
          to="/"
          aria-label="Teamstar home"
          style={{
            cursor: "pointer",
            marginLeft: screens.md ? "5%" : 0,
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Image
            src={HeaderLogo}
            alt="Teamstar"
            preview={false}
            className="header-logo"
            style={{
              height: screens.sm && !screens.xl ? 56 : 64,
              width: "auto",
              display: "block",
            }}
          />
        </Link>

        {/* Right: Only burger icon (for mobile) */}
        {!screens.xl && (
          <MenuOutlined
            style={{ fontSize: 22, color: "#4B5563", cursor: "pointer" }}
            onClick={() => {
              setDrawerOpen(true);
              setCartOpen(false);
            }}
          />
        )}

        {/* Desktop menu — hidden on mobile */}
        {screens.xl && (
          <>
            <Menu
              mode="horizontal"
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={handleMenuClick}
              style={{
                background: "transparent",
                lineHeight: "64px",
                flex: 1,
                minWidth: 0,
                marginLeft: "1%",
                fontWeight: "600",
              }}
            />

            <Space size="large" style={{ padding: "16px", marginRight: "5%" }}>
              <Badge>
                <Flex
                  vertical
                  justify="center"
                  align="center"
                  onClick={openSearch}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openSearch();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Search products"
                  className="drawer-icon"
                  style={{ width: 40 }}
                >
                  <SearchOutlined
                    style={{
                      fontSize: 18,
                      height: 20,
                      width: 20,
                      cursor: "pointer",
                      color: "#4B5563",
                      fontWeight: "600",
                    }}
                  />

                  <span
                    style={{
                      cursor: "pointer",
                      height: 16,
                      fontSize: 12,
                      color: "#4B5563",
                    }}
                  >
                    <b>Search</b>
                  </span>
                </Flex>
              </Badge>

              <Badge
                count={badgeCount}
                size="small"
                style={{
                  backgroundColor: "black",
                  borderColor: "black",
                  color: "white",
                  fontSize: 8,
                }}
              >
                <Flex
                  vertical
                  justify="center"
                  align="center"
                  onClick={() => setCartOpen(true)}
                  className="drawer-icon"
                  style={{ width: 40 }}
                >
                  <ShoppingCartOutlined
                    style={{
                      fontSize: 20,
                      cursor: "pointer",
                      color: "#4B5563",
                      fontWeight: "600",
                    }}
                  />

                  <b
                    style={{
                      cursor: "pointer",
                      height: 16,
                      fontSize: 12,
                      color: "#4B5563",
                    }}
                  >
                    Cart
                  </b>
                </Flex>
              </Badge>

              <Badge>
                <Flex
                  vertical
                  justify="center"
                  align="center"
                  onClick={() => handleNavigate("/profile")}
                  className="drawer-icon"
                  style={{ width: 40 }}
                >
                  <UserOutlined
                    style={{
                      fontSize: 20,
                      cursor: "pointer",
                      color: "#4B5563",
                      fontWeight: "600",
                    }}
                  />

                  <span
                    style={{
                      cursor: "pointer",
                      height: 16,
                      fontSize: 12,
                      color: "#4B5563",
                    }}
                  >
                    <b>Profile</b>
                  </span>
                </Flex>
              </Badge>
            </Space>
          </>
        )}
      </Flex>

      {/* Drawer for mobile — navigation + icons */}
      <Drawer
        zIndex={998}
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        closable={false}
        width={260}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 16px",
          }}
        >
          <CloseOutlined
            onClick={() => setDrawerOpen(false)}
            className="drawer-icon"
          />
        </div>

        <Menu
          mode="vertical"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />

        <Space size="large" style={{ padding: "16px" }}>
          <Button
            type="text"
            icon={<SearchOutlined />}
            aria-label="Search products"
            className="drawer-icon"
            style={{ fontSize: 20, cursor: "pointer", color: "#4B5563" }}
            onClick={openSearch}
          />

          <Badge
            count={badgeCount}
            size="small"
            style={{
              backgroundColor: "black",
              borderColor: "black",
              color: "white",
              fontSize: 10,
            }}
          >
            <ShoppingCartOutlined
              className="drawer-icon"
              style={{ fontSize: 20, cursor: "pointer", color: "#4B5563" }}
              onClick={() => {
                setDrawerOpen(false);
                setCartOpen(true);
              }}
            />
          </Badge>

          <UserOutlined
            className="drawer-icon"
            style={{ fontSize: 20, cursor: "pointer", color: "#4B5563" }}
            onClick={() => handleNavigate("/profile")}
          />
        </Space>
      </Drawer>

      <Modal
        centered
        title="Search products"
        open={searchOpen}
        onCancel={closeSearch}
        footer={null}
        width={600}
        destroyOnClose={true}
        afterOpenChange={(open) => {
          if (open) searchInputRef.current?.focus();
        }}
      >
        <Search
          ref={searchInputRef}
          autoFocus
          placeholder="Search products..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onSearch={(value) => getSearchProducts(value)}
          enterButton
          loading={isSearchLoading}
          style={{ width: "100%", marginBottom: 16 }}
        />

        <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
          <List
            loading={isSearchLoading}
            dataSource={searchProducts}
            rowKey="Id"
            locale={{
              emptyText: isSearchLoading
                ? null
                : searchQuery === null
                  ? "Search products by name or SKU."
                  : "No product(s) available.",
            }}
            renderItem={(item) => {
              const firstImage = (item.ImagesTag || "").split(";")[0].trim();

              return (
                <List.Item>
                  <Link
                    to={getProductUrl(item.ProductName, item.Sku, item.Id)}
                    onClick={closeSearch}
                    style={{ display: "block", width: "100%" }}
                  >
                    <List.Item.Meta
                      avatar={
                        firstImage ? (
                          <Image
                            src={settings.productImageFolder + firstImage}
                            alt={item.ProductName}
                            preview={false}
                            width={64}
                            height={64}
                            style={{ borderRadius: 4, objectFit: "contain" }}
                          />
                        ) : null
                      }
                      title={
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#002753",
                            fontSize: 16,
                          }}
                        >
                          {item.ProductName}
                        </span>
                      }
                      description={`SKU: ${item.Sku}`}
                    />
                  </Link>
                </List.Item>
              );
            }}
          />
        </div>

        {!isSearchLoading && (
          <Flex justify="center" style={{ marginTop: 16 }}>
            <Pagination
              size="small"
              current={searchPagination.current}
              pageSize={searchPagination.pageSize}
              total={searchPagination.total}
              showSizeChanger={false}
              hideOnSinglePage
              onChange={(page) => getSearchProducts(searchQuery, page)}
            />
          </Flex>
        )}
      </Modal>

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        setOpen={setCartOpen}
      />
    </Header>
  );
};

export default AppHeader;
